// =============================================================================
// SIREPPAR - Aplicación Principal
// =============================================================================

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, ProtectedRoute } from './contexts/AuthContext';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AfiliadosControlPage from './pages/internal/AfiliadosControlPage';
import AdherentesPage from './pages/internal/AdherentesPage';
import VerificacionAutenticidadPage from './pages/internal/VerificacionAutenticidadPage';
import NotasAfiliadosDuplicadosPage from './pages/internal/NotasAfiliadosDuplicadosPage';

// Layouts
import MainLayout from './components/layout/MainLayout';

// Páginas públicas
import LoginPage from './pages/external/LoginPage';
import VerificarConstanciaPage from './pages/external/VerificarConstanciaPage';
import ConstitucionPartidoPage from './pages/external/ConstitucionPartidoPage';
import ConstitucionAlianzaPage from './pages/external/ConstitucionAlianzaPage';
import ReempadronamientoPage from './pages/external/ReempadronamientoPage';
import ReempadronamientoCerradoPage from './pages/external/ReempadronamientoCerradoPage';
import MisIntimacionesPage from './pages/external/MisIntimacionesPage';
import ReconocimientoDocumentacionPage from './pages/external/ReconocimientoDocumentacionPage';
import ReconocimientoAutoridadesPage from './pages/external/ReconocimientoAutoridadesPage';
import CorregirAtributosPage from './pages/external/CorregirAtributosPage';
import ApoderadoPanelPage from './pages/external/ApoderadoPanelPage';
import ConsultasPage from './pages/external/ConsultasPage';
import ConsultaNuevaPage from './pages/external/ConsultaNuevaPage';
import ConsultaDetailPage from './pages/external/ConsultaDetailPage';
import { CONSULTAS_HABILITADAS, ROLES_CONSULTAS_PARTIDO, ROLES_ESCRITURA_CONSULTAS_SEP } from './utils/consultas';
import CandidaturaWizardPage from './pages/external/candidatura-wizard/CandidaturaWizardPage';

// Páginas protegidas
import DashboardPage from './pages/internal/DashboardPage';
import PartidosListPage from './pages/internal/PartidosListPage';
import PartidoDetailPage from './pages/internal/PartidoDetailPage';
import PartidoFormPage from './pages/internal/PartidoFormPage';
import MiPartidoPage from './pages/internal/MiPartidoPage';
import ExpedientesListPage from './pages/internal/ExpedientesListPage';
import ExpedienteDetailPage from './pages/internal/ExpedienteDetailPage';
import ExpedienteOCandidaturaGate from './pages/internal/ExpedienteOCandidaturaGate';
import ExpedienteFormPage from './pages/internal/ExpedienteFormPage';
import AdmisibilidadPage from './pages/internal/AdmisibilidadPage';
import GestionExpedientesPage from './pages/internal/GestionExpedientesPage';
import BandejaAlianzasPage from './pages/internal/BandejaAlianzasPage';
import GestionConsultasPage from './pages/internal/GestionConsultasPage';
import GestionConsultaDetailPage from './pages/internal/GestionConsultaDetailPage';
import GestionConsultaNuevaPage from './pages/internal/GestionConsultaNuevaPage';
import DocumentosPage from './pages/internal/DocumentosPage';
import AutoridadesPage from './pages/internal/AutoridadesPage';
import AfiliadosPage from './pages/internal/AfiliadosPage';
import UsuariosPage from './pages/internal/UsuariosPage';
import ReportesPage from './pages/internal/ReportesPage';
import ConfiguracionPage from './pages/internal/ConfiguracionPage';
import PerfilPage from './pages/internal/PerfilPage';
import PadronesPage from './pages/internal/PadronesPage';
import InteraccionesPage from './pages/internal/InteraccionesPage';
import InteraccionFormPage from './pages/internal/InteraccionFormPage';
import InteraccionDetailPage from './pages/internal/InteraccionDetailPage';
import { ROLES_ESCRITURA_INTERACCIONES } from './utils/permisosInteracciones';
import FinanciamientoPage from './pages/internal/FinanciamientoPage';
import CuentaBancariaPage from './pages/internal/CuentaBancariaPage';
import RendicionAnualPage from './pages/internal/RendicionAnualPage';
import RendicionCampanaPage from './pages/internal/RendicionCampanaPage';
import AgrupacionesHabilitadasPage from './pages/internal/AgrupacionesHabilitadasPage';
import BandejaCandidaturasPage from './pages/internal/BandejaCandidaturasPage';
import ExpedienteCandidaturaDetailPage from './pages/internal/ExpedienteCandidaturaDetailPage';
import LotesAfiliacionPage from './pages/external/LotesAfiliacionPage';
import LoteAfiliacionPage from './pages/external/LoteAfiliacionPage';
import LotesAfiliacionSepPage from './pages/internal/LotesAfiliacionSepPage';
import LoteAfiliacionSepPage from './pages/internal/LoteAfiliacionSepPage';

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Rutas públicas */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reempadronamiento" element={import.meta.env.VITE_REEMPADRONAMIENTO_HABILITADO === 'true' ? <ReempadronamientoPage /> : <ReempadronamientoCerradoPage />} />
            <Route path="/mis-intimaciones" element={<Navigate to="/expedientes" replace />} />
            {/* Las rutas van detrás del MISMO flag que la tarjeta del login: si solo
                se escondiera la tarjeta, el trámite seguiría accesible escribiendo la
                URL a mano. Con el flag en false redirigen al login. */}
            <Route path="/constitucion" element={import.meta.env.VITE_CONSTITUCION_HABILITADA === 'true' ? <ConstitucionPartidoPage /> : <Navigate to="/login" replace />} />
            {/* Constitución de alianza: /nueva elige convocatoria y crea el borrador;
                /:alianzaId es el link con el que se retoma ese borrador. */}
            <Route path="/alianzas/nueva" element={import.meta.env.VITE_ALIANZAS_HABILITADAS === 'true' ? <ConstitucionAlianzaPage /> : <Navigate to="/login" replace />} />
            <Route path="/alianzas/:alianzaId" element={import.meta.env.VITE_ALIANZAS_HABILITADAS === 'true' ? <ConstitucionAlianzaPage /> : <Navigate to="/login" replace />} />
            <Route path="/verificar/:codigo" element={<VerificarConstanciaPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Wizard de documentación de reconocimiento (autenticado, full-screen fuera del layout) */}
            <Route
              path="/reconocimiento/documentacion"
              element={
                <ProtectedRoute roles={['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE']}>
                  <ReconocimientoDocumentacionPage />
                </ProtectedRoute>
              }
            />

            {/* Wizard de autoridades definitivas del reconocimiento (etapa AUTORIDADES_PENDIENTES) */}
            <Route
              path="/reconocimiento/autoridades"
              element={
                <ProtectedRoute roles={['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE']}>
                  <ReconocimientoAutoridadesPage />
                </ProtectedRoute>
              }
            />
            {/* Rutas protegidas */}
            <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* Consultas a la SEP (#388). Detrás de VITE_CONSULTAS_HABILITADAS. */}
              <Route path="consultas" element={CONSULTAS_HABILITADAS ? <ProtectedRoute roles={ROLES_CONSULTAS_PARTIDO}><ConsultasPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="consultas/nueva" element={CONSULTAS_HABILITADAS ? <ProtectedRoute roles={ROLES_CONSULTAS_PARTIDO}><ConsultaNuevaPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="consultas/:id" element={CONSULTAS_HABILITADAS ? <ProtectedRoute roles={ROLES_CONSULTAS_PARTIDO}><ConsultaDetailPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="reconocimiento/corregir-atributos" element={<ProtectedRoute roles={['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE']}><CorregirAtributosPage /></ProtectedRoute>} />
              
              {/* Partidos */}
              <Route path="partidos" element={<ProtectedRoute roles={ROLES_INTERNOS}><PartidosListPage /></ProtectedRoute>} />
              <Route path="partidos/nuevo" element={<ProtectedRoute roles={ROLES_INTERNOS}><PartidoFormPage /></ProtectedRoute>} />
              <Route path="partidos/:id" element={<PartidoDetailPage />} />
              <Route path="partidos/:id/editar" element={<ProtectedRoute roles={ROLES_INTERNOS}><PartidoFormPage /></ProtectedRoute>} />
              <Route path="partidos/:id/autoridades" element={<ProtectedRoute roles={ROLES_INTERNOS}><AutoridadesPage /></ProtectedRoute>} />
              <Route path="mi-partido" element={<MiPartidoPage />} />
              
              {/* Expedientes */}
              <Route path="expedientes" element={<ExpedientesListPage />} />
              <Route path="expedientes/nuevo" element={<ExpedienteFormPage />} />
              <Route path="expedientes/:id" element={<ExpedienteOCandidaturaGate />} />
              <Route path="apoderado/panel" element={<ProtectedRoute roles={['APODERADO_FIRMANTE']}><ApoderadoPanelPage /></ProtectedRoute>} />
              <Route path="apoderado/candidatura/:id" element={<ProtectedRoute roles={['APODERADO_FIRMANTE']}><CandidaturaWizardPage /></ProtectedRoute>} />
              <Route path="admisibilidad" element={<ProtectedRoute roles={ROLES_INTERNOS}><AdmisibilidadPage /></ProtectedRoute>} />
              <Route path="gestion" element={<ProtectedRoute roles={ROLES_INTERNOS}><GestionExpedientesPage /></ProtectedRoute>} />
              <Route path="gestion/alianzas" element={<ProtectedRoute roles={ROLES_INTERNOS}><BandejaAlianzasPage /></ProtectedRoute>} />
              {/* Consultas de partidos: circuito de la SEP (#390). No va en /gestion/...: marcaría activa la Bandeja de trabajo. */}
              <Route path="gestion-consultas" element={CONSULTAS_HABILITADAS ? <ProtectedRoute roles={ROLES_INTERNOS}><GestionConsultasPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="gestion-consultas/nueva" element={CONSULTAS_HABILITADAS ? <ProtectedRoute roles={ROLES_ESCRITURA_CONSULTAS_SEP}><GestionConsultaNuevaPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="gestion-consultas/:id" element={CONSULTAS_HABILITADAS ? <ProtectedRoute roles={ROLES_INTERNOS}><GestionConsultaDetailPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              
              {/* Documentos */}
              <Route path="documentos" element={<DocumentosPage />} />
              <Route path="documentos/subir" element={<DocumentosPage />} />
              
              {/* Trámites del partido */}
              <Route path="autoridades" element={<AutoridadesPage />} />
              <Route path="afiliados" element={<AfiliadosPage />} />
              <Route path="afiliados-control" element={<ProtectedRoute roles={ROLES_INTERNOS}><AfiliadosControlPage /></ProtectedRoute>} />
              <Route path="adherentes" element={<ProtectedRoute roles={ROLES_INTERNOS}><AdherentesPage /></ProtectedRoute>} />
              <Route path="verificacion-autenticidad" element={import.meta.env.VITE_AUTENTICIDAD_HABILITADA === 'true' ? <ProtectedRoute roles={ROLES_INTERNOS}><VerificacionAutenticidadPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="lotes-afiliacion-sep/:id" element={import.meta.env.VITE_AUTENTICIDAD_HABILITADA === 'true' ? <ProtectedRoute roles={ROLES_INTERNOS}><LoteAfiliacionSepPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="lotes-afiliacion-sep" element={import.meta.env.VITE_AUTENTICIDAD_HABILITADA === 'true' ? <ProtectedRoute roles={ROLES_INTERNOS}><LotesAfiliacionSepPage /></ProtectedRoute> : <Navigate to="/dashboard" replace />} />
              <Route path="lotes-afiliacion/:id" element={import.meta.env.VITE_AUTENTICIDAD_HABILITADA === 'true' ? <LoteAfiliacionPage /> : <Navigate to="/dashboard" replace />} />
              <Route path="lotes-afiliacion" element={import.meta.env.VITE_AUTENTICIDAD_HABILITADA === 'true' ? <LotesAfiliacionPage /> : <Navigate to="/dashboard" replace />} />
              <Route path="notas-duplicados" element={<ProtectedRoute roles={ROLES_INTERNOS}><NotasAfiliadosDuplicadosPage /></ProtectedRoute>} />

              {/* Administración */}
              <Route path="usuarios" element={<ProtectedRoute roles={['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE']}><UsuariosPage /></ProtectedRoute>} />
              <Route path="padrones" element={<ProtectedRoute roles={['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ANALISTA']}><PadronesPage /></ProtectedRoute>} />
              <Route path="reportes" element={<ProtectedRoute roles={['ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE']}><ReportesPage /></ProtectedRoute>} />
              <Route path="configuracion" element={<ProtectedRoute roles={['ADMINISTRADOR']}><ConfiguracionPage /></ProtectedRoute>} />
              <Route path="gaiman/agrupaciones-habilitadas" element={<ProtectedRoute roles={['SUPERVISOR', 'ADMINISTRADOR']}><AgrupacionesHabilitadasPage /></ProtectedRoute>} />
              <Route path="candidaturas/gaiman/bandeja" element={<ProtectedRoute roles={['ANALISTA', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ADMINISTRADOR', 'MESA_ENTRADAS']}><BandejaCandidaturasPage /></ProtectedRoute>} />
              <Route path="candidaturas/gaiman/expedientes/:id" element={<ProtectedRoute roles={['ANALISTA', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ADMINISTRADOR', 'MESA_ENTRADAS']}><ExpedienteCandidaturaDetailPage /></ProtectedRoute>} />

              {/* Financiamiento */}
              <Route path="financiamiento" element={<FinanciamientoPage />} />
              <Route path="financiamiento/cuenta-bancaria/:partidoId" element={<CuentaBancariaPage />} />
              <Route path="financiamiento/rendicion-anual/:partidoId" element={<ProtectedRoute roles={ROLES_INTERNOS}><RendicionAnualPage /></ProtectedRoute>} />
              <Route path="financiamiento/rendicion-campana/:partidoId" element={<ProtectedRoute roles={ROLES_INTERNOS}><RendicionCampanaPage /></ProtectedRoute>} />

              {/* Interacciones */}
              <Route path="interacciones" element={<ProtectedRoute roles={ROLES_INTERNOS}><InteraccionesPage /></ProtectedRoute>} />
              <Route path="interacciones/nueva" element={<ProtectedRoute roles={ROLES_ESCRITURA_INTERACCIONES}><InteraccionFormPage /></ProtectedRoute>} />
              <Route path="interacciones/:id" element={<ProtectedRoute roles={ROLES_INTERNOS}><InteraccionDetailPage /></ProtectedRoute>} />
              <Route path="interacciones/:id/editar" element={<ProtectedRoute roles={ROLES_ESCRITURA_INTERACCIONES}><InteraccionFormPage /></ProtectedRoute>} />
              
              {/* Perfil */}
              <Route path="perfil" element={<PerfilPage />} />
            </Route>
            
            {/* 403 */}
            <Route path="/unauthorized" element={
              <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">403</h1>
                  <p className="text-gray-600 mb-4">No tiene permisos para acceder a esta página.</p>
                  <a href="/dashboard" className="text-primary-600">Volver al inicio</a>
                </div>
              </div>
            } />
            
            {/* 404 */}
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-4">Página no encontrada.</p>
                  <a href="/dashboard" className="text-primary-600">Volver al inicio</a>
                </div>
              </div>
            } />
          </Routes>
          
          <Toaster position="top-right" toastOptions={{
            duration: 4000,
            style: { background: '#363636', color: '#fff' },
            success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
            error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
          }} />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
